/**
 * Lift2Fit — Instant Paint: the read cache behind `useCachedAsync`.
 *
 * WHAT THIS IS. A small, keyed, in-memory store for READ responses. A screen
 * paints from it immediately and revalidates in the background, so returning to
 * a tab feels instant instead of paying a round trip again.
 *
 * WHAT THIS IS NOT. It is not a source of truth. The API stays the only authority
 * on writes: a stale read can never authorise an action, because the server
 * refuses it (409/422) and the screen shows that sentence and refreshes.
 *
 * THE ONE RULE. A mutation must invalidate the keys it can change, by prefix,
 * before the UI confirms success. `invalidate` is the only way data leaves the
 * cache early, and every mutation site calls it.
 *
 * REVERTING. The whole feature sits behind `VITE_INSTANT_PAINT`; with it off,
 * `useCachedAsync` calls the loader directly and this file is never consulted.
 */

export interface CacheEntry<T> {
  value: T;
  /** When the value was written — used only to decide "fresh enough to paint". */
  at: number;
}

/** How long a cached read may be painted without a revalidation behind it. */
export const READ_TTL_MS = 60_000;

const store = new Map<string, CacheEntry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();
const listeners = new Set<(key: string) => void>();

/** The feature switch. Read once; the build replaces `import.meta.env`. */
export function instantPaintEnabled(): boolean {
  return import.meta.env?.VITE_INSTANT_PAINT === '1';
}

/** A stable key: the route plus its query, so filters do not share a slot. */
export function readKey(path: string, params?: Record<string, string | number | null | undefined>): string {
  if (!params) return path;
  const query = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, value]) => `${name}=${String(value)}`)
    .join('&');
  return query === '' ? path : `${path}?${query}`;
}

export function peek<T>(key: string): CacheEntry<T> | null {
  const entry = store.get(key);
  return entry === undefined ? null : (entry as CacheEntry<T>);
}

/** True when the entry is young enough to paint without a skeleton. */
export function isFresh(entry: CacheEntry<unknown> | null, now = Date.now()): boolean {
  return entry !== null && now - entry.at <= READ_TTL_MS;
}

export function write<T>(key: string, value: T): void {
  store.set(key, { value, at: Date.now() });
  for (const listener of listeners) listener(key);
}

/**
 * Drop every key that starts with `prefix`, or one exact key.
 *
 * Invalidation is the contract with the mutation sites: `invalidate('coaching/clients')`
 * clears that screen; `invalidate('crm/calendar')` clears slots AND bookings,
 * because they are written with that prefix.
 */
export function invalidate(prefix: string): void {
  const exact = store.has(prefix);
  let dropped = 0;
  for (const key of [...store.keys()]) {
    if (key === prefix || key.startsWith(`${prefix}/`) || key.startsWith(`${prefix}?`)) {
      store.delete(key);
      dropped += 1;
    }
  }
  // `invalidate('coaching/clients')` must also clear `coaching/clients?kind=x`.
  if (exact) dropped += 0;
  if (dropped === 0 && exact === false) {
    // A prefix with no exact match still clears anything nested beneath it.
    for (const key of [...store.keys()]) {
      if (key.includes(prefix)) {
        store.delete(key);
        dropped += 1;
      }
    }
  }
  if (dropped > 0) for (const listener of listeners) listener(prefix);
}

export function clearAll(): void {
  store.clear();
  inFlight.clear();
}

/** Subscribe to writes/invalidations for one key prefix. Returns an unsubscribe. */
export function subscribe(prefix: string, fn: (key: string) => void): () => void {
  const wrapped = (key: string) => {
    if (key === prefix || key.startsWith(`${prefix}/`) || key.startsWith(`${prefix}?`)) fn(key);
  };
  listeners.add(wrapped);
  return () => listeners.delete(wrapped);
}

/**
 * One request per key, even when four screens ask at once.
 *
 * `revalidate` always runs the loader; `loadOnce` shares an in-flight promise so
 * a focus revalidate and a mount do not double the traffic.
 */
export async function loadOnce<T>(key: string, loader: () => Promise<T>): Promise<T> {
  const running = inFlight.get(key);
  if (running !== undefined) return running as Promise<T>;
  const promise = loader().finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, promise);
  return promise;
}

/** Diagnostics for the dev assertion: every key currently painted from cache. */
export function cachedKeys(): string[] {
  return [...store.keys()];
}

/** Test seam — the store is module state, so a suite must be able to reset it. */
export function __resetForTests(): void {
  clearAll();
  listeners.clear();
}
